import { Box } from '@mui/material'
import React from 'react'
import { H3, H4, ListItem, Paragraph, Strong, UnorderedList } from '../MethodologyAndMetrica.styled'
import DropDown from '../components/DropDown'
import 'katex/dist/katex.min.css'
import { InlineMath, BlockMath } from 'react-katex'

const InformationSection = () => {
  return (
    <Box>
      <H3>Methodology</H3>
      <Paragraph>
        The index construction process involves several key steps: normalizing the data, calculating the pillar scores,
        aggregating these scores to form the overall AI Vibrancy Index, and determining the Global AI Vibrancy Ranking
        for countries each year. Below is a detailed explanation of each step.
      </Paragraph>
      <H4>Step 1: Normalization</H4>
      <Paragraph>
        To ensure comparability across different scales and units of measurement, min-max normalization is applied to
        all indicators. This method scales the values within the [0, 100] range. The normalized value for the indicator
        is calculated using the following formula:
      </Paragraph>
      <Paragraph>
        <span className='custom-math-style'>
          <BlockMath
            math={
              'x_{ijk} = \\frac{x_{ijk}^{\\text{raw}} - x_{ij,\\text{min}}}{x_{ij,\\text{max}} - x_{ij,\\text{min}}} \\cdot 100'
            }
          ></BlockMath>
        </span>
      </Paragraph>
      <Paragraph>
        where:
        <UnorderedList>
          <ListItem>
            <InlineMath math={'x_{ijk}^{\\text{raw}}'} /> is the original value of the{' '}
            <InlineMath math={'i^{\\text{th}}'} /> indicator for pillar <InlineMath math={'j'} /> and country{' '}
            <InlineMath math={'k'} />.
          </ListItem>
          <ListItem>
            <InlineMath math={'x_{ij,\\text{min}}'} /> and <InlineMath math={'x_{ij,\\text{max}}'} /> are the minimum
            and maximum values of indicator <InlineMath math={'i'} /> in pillar <InlineMath math={'j'} /> across all
            countries.
          </ListItem>
        </UnorderedList>
      </Paragraph>
      <H4>Step 2: Pillar Score Calculation</H4>
      <Paragraph>
        The score of pillar <InlineMath math={'j'} /> for country <InlineMath math={'k'} />, denoted as{' '}
        <InlineMath math={'p_{jk}'} />, is computed as the weighted average of various indicators associated with the
        pillar and the country. The formula is:
      </Paragraph>
      <Paragraph>
        <BlockMath
          math={'p_{jk} = \\frac{\\sum_{i=1}^{N_{j}}(w_{ij}\\cdot x_{ijk})}{\\sum_{i=1}^{N_{j}}w_{ij}}'}
        ></BlockMath>
      </Paragraph>
      <Paragraph>
        where:
        <UnorderedList>
          <ListItem>
            <InlineMath math={'N_{j}'} /> is the number of indicators for pillar <InlineMath math={'j'} />.
          </ListItem>
          <ListItem>
            <InlineMath math={'w_{ij}'} /> is the weight assigned to the <InlineMath math={'i^{\\text{th}}'} />{' '}
            indicator for pillar <InlineMath math={'j'} />, ranging from 0 to 10, with higher values indicating greater
            importance of the indicator. We assume that these weights are non-negative and their sum is non-zero.
          </ListItem>
          <ListItem>
            <InlineMath math={'x_{ijk}'} /> is the normalized score of the <InlineMath math={'i^{\\text{th}}'} />{' '}
            indicator for pillar <InlineMath math={'j'} /> and country <InlineMath math={'k'} />.
          </ListItem>
        </UnorderedList>
      </Paragraph>
      <H4>Step 3: AI Vibrancy Index Calculation</H4>
      <Paragraph>
        The AI Vibrancy Index for a country <InlineMath math={'k'} />, denoted as <InlineMath math={'V_{k}'} />, is
        calculated as the weighted average of the scores of all the pillars using the formula:
      </Paragraph>
      <Paragraph>
        <BlockMath math={'V_{k} = \\frac{\\sum_{j=1}^{M}(W_{j}\\cdot p_{jk})}{\\sum_{j=1}^{M}W_{j}}'}></BlockMath>
      </Paragraph>
      <Paragraph>
        where:
        <UnorderedList>
          <ListItem>
            <InlineMath math={'M'} /> is the total number of pillars.
          </ListItem>
          <ListItem>
            <InlineMath math={'W_{j}'} /> is the weight assigned to pillar <InlineMath math={'j'} />, ranging from 0 to
            10, with higher values indicating greater importance of the pillar. We assume that these weights are
            non-negative and their sum is non-zero.
          </ListItem>
          <ListItem>
            <InlineMath math={'p_{jk}'} /> is the score of pillar <InlineMath math={'j'} /> for country{' '}
            <InlineMath math={'k'} />, as calculated above.
          </ListItem>
        </UnorderedList>
      </Paragraph>
      <H4>Step 4: Ranking Countries</H4>
      <Paragraph>
        After calculating the AI Vibrancy Index for each country, the countries are ranked for each year by their
        resulting weighted index scores. This ranking offers an annual comparison of AI vibrancy across different
        countries, whether in absolute or per capita terms.
      </Paragraph>
      <Paragraph>
        The same process is applied to each sub-index: Innovation, Economic Competitiveness, and Policy, Governance, and
        Public Engagement. Only the relevant indicators are aggregated, ensuring each sub-index provides a focused
        analysis of a specific AI vibrancy dimension. For more details, refer to the <i>Global AI Vibrancy Tool</i>{' '}
        paper.
      </Paragraph>
      <H3>Metric Definition</H3>

      <DropDown
        headerComponent={<H4 style={{ display: 'inline' }}>Research and Development</H4>}
        bodyComponent={
          <Paragraph>
            <Strong>AI Journal Publications</Strong>:&nbsp;Number of published AI journal publications in a given
            country.&nbsp;
            <em>Source:&nbsp;Center for Security and Emerging Technology, 2024. Data last updated in August 2024.</em>
            <br />
            <br />
            <Strong>AI Conference Publications</Strong>:&nbsp;Number of published AI conference publications in a given
            country.&nbsp;
            <em>Source:&nbsp;Center for Security and Emerging Technology, 2024. Data last updated in August 2024.</em>
            <br />
            <br />
            <Strong>AI Journal Citations</Strong>:&nbsp;Number of published AI journal citations in a given
            country.&nbsp;
            <em>Source:&nbsp;Center for Security and Emerging Technology, 2024. Data last updated in August 2024.</em>
            <br />
            <br />
            <Strong>AI Conference Citations</Strong>:&nbsp;Number of published AI conference citations in a given
            country.&nbsp;
            <em>Source:&nbsp;Center for Security and Emerging Technology, 2024. Data last updated in August 2024.</em>
            <br />
            <br />
            <Strong>AI Patents Grants</Strong>:&nbsp;Number of AI patent grants in a given country.&nbsp;
            <em>Source:&nbsp;Center for Security and Emerging Technology, 2024. Data last updated in August 2024.</em>
            <br />
            <br />
            <Strong>Notable Machine Learning Models</Strong>:&nbsp;Number of notable machine learning models in a given
            country.&nbsp;
            <em>
              Source:&nbsp;Epoch AI, 2023. Data last updated in March 2024. For the most recent data snapshot, please
              refer to the Epoch AI’s website.
            </em>
            <br />
            <br />
            <Strong>Academia-Industry Model Production Concentration</Strong>:&nbsp;Indicator quantifying the balance
            between the number of notable machine learning models produced by academia and industry within each
            country.&nbsp;<em>Source:&nbsp;Epoch AI, 2023; AI Index, 2024. Data last updated in March 2024.</em>
            <br />
            <br />
            <Strong>Foundation Models</Strong>:&nbsp;Number of foundation models in a given country.&nbsp;
            <em>
              Source:&nbsp;Bommasani et al., 2023; AI Index, 2024. Data last updated in March 2024. For the most recent
              data snapshot, please refer to the Stanford CRFM’s website.
            </em>
            <br />
            <br />
            <Strong>Foundation Models Datasets</Strong>:&nbsp;Number of foundation models’ datasets in a given
            country.&nbsp;
            <em>
              Source:&nbsp;Bommasani et al., 2023; AI Index, 2024. Data last updated in March 2024. For the most recent
              data snapshot, please refer to the Stanford CRFM’s website.
            </em>
            <br />
            <br />
            <Strong>Foundation Models Applications</Strong>:&nbsp;Number of foundation models’ applications in a given
            country.&nbsp;
            <em>
              Source:&nbsp;Bommasani et al., 2023; AI Index, 2024. Data last updated in March 2024. For the most recent
              data snapshot, please refer to the Stanford CRFM’s website.
            </em>
            <br />
            <br />
            <Strong>Open Access Foundation Models</Strong>:&nbsp;The proportion of foundation models that are openly
            accessible compared to the total number of foundation models in a given country.&nbsp;
            <em>
              Source:&nbsp;Bommasani et al., 2023; AI Index, 2024. Data last updated in March 2024. For the most recent
              data snapshot, please refer to the Stanford CRFM’s website.
            </em>
            <br />
            <br />
            <Strong>AI GitHub Projects</Strong>:&nbsp;Total number of AI-related projects on GitHub in a given country,
            consisting of a collection of files such as source code, documentation, configuration files, and images that
            together form a software project.&nbsp;
            <em>Source:&nbsp;GitHub, 2023. Data last updated in March 2024.</em>
            <br />
            <br />
            <Strong>AI GitHub Projects Stars</Strong>:&nbsp;Total number of stars for AI-related projects on GitHub in a
            given country. Users can "star" a repository to show interest, similar to liking a post on social media,
            indicating support for the project.&nbsp;
            <em>Source:&nbsp;GitHub, 2023. Data last updated in March 2024.</em>
          </Paragraph>
        }
      />

      <DropDown
        headerComponent={<H4 style={{ display: 'inline' }}>Responsible AI</H4>}
        bodyComponent={
          <Paragraph>
            <Strong>AAAI Conference Submissions on RAI Topics</Strong>:&nbsp;Number of responsible AI-related academic
            submissions to the AAAI conference in a given country. The AI Index explores four key dimensions of
            responsible AI: privacy and data governance, transparency and explainability, security and safety, and
            fairness.&nbsp;<em>Source:&nbsp;AI Index, 2024. Data last updated in March 2024.</em>
            <br />
            <br />
            <Strong>AIES Conference Submissions on RAI Topics</Strong>:&nbsp;Number of responsible AI-related academic
            submissions to the AIES conference in a given country. The AI Index explores four key dimensions of
            responsible AI: privacy and data governance, transparency and explainability, security and safety, and
            fairness.&nbsp;<em>Source:&nbsp;AI Index, 2024. Data last updated in March 2024.</em>
            <br />
            <br />
            <Strong>FAccT Conference Submissions on RAI Topics</Strong>:&nbsp;Number of responsible AI-related academic
            submissions to the FAccT conference in a given country. The AI Index explores four key dimensions of
            responsible AI: privacy and data governance, transparency and explainability, security and safety, and
            fairness.&nbsp;
            <em>Source:&nbsp;AI Index, 2024. Data last updated in March 2024.</em>
            <br />
            <br />
            <Strong>ICLR Conference Submissions on RAI Topics</Strong>:&nbsp;Number of responsible AI-related academic
            submissions to the ICLR conference in a given country. The AI Index explores four key dimensions of
            responsible AI: privacy and data governance, transparency and explainability, security and safety, and
            fairness.&nbsp;<em>Source:&nbsp;AI Index, 2024. Data last updated in March 2024.</em>
            <br />
            <br />
            <Strong>ICML Conference Submissions on RAI Topics</Strong>:&nbsp;Number of responsible AI-related academic
            submissions to the ICML conference in a given country. The AI Index explores four key dimensions of
            responsible AI: privacy and data governance, transparency and explainability, security and safety, and
            fairness.&nbsp;<em>Source:&nbsp;AI Index, 2024. Data last updated in March 2024.</em>
            <br />
            <br />
            <Strong>NeurIPS Conference Submissions on RAI Topics</Strong>:&nbsp;Number of responsible AI-related
            academic submissions to the NeurIPS conference in a given country. The AI Index explores four key dimensions
            of responsible AI: privacy and data governance, transparency and explainability, security and safety, and
            fairness.&nbsp;<em>Source:&nbsp;AI Index, 2024. Data last updated in March 2024.</em>
          </Paragraph>
        }
      />

      <DropDown
        headerComponent={<H4 style={{ display: 'inline' }}>Economy</H4>}
        bodyComponent={
          <Paragraph>
            <Strong>Total AI Private Investment</Strong>:&nbsp;Total amount of private investment received for AI
            startups (nominal USD) in a given country.&nbsp;
            <em>Source:&nbsp;Quid, 2023. Data last updated in March 2024.</em>
            <br />
            <br />
            <Strong>Total AI Merger/Acquisition Investment</Strong>:&nbsp;Total amount of merger/acquisition investment
            received for AI startups (nominal USD) in a given country.&nbsp;
            <em>Source:&nbsp;Quid, 2023. Data last updated in March 2024.</em>
            <br />
            <br />
            <Strong>Total AI Minority Stake Investment</Strong>:&nbsp;Total amount of minority stake investment received
            for AI startups (nominal USD) in a given country.&nbsp;
            <em>Source:&nbsp;Quid, 2023. Data last updated in March 2024.</em>
            <br />
            <br />
            <Strong>Total AI Public Offering Investment</Strong>:&nbsp;Total amount of public offering investment
            received for AI startups (nominal USD) in a given country.&nbsp;
            <em>Source:&nbsp;Quid, 2023. Data last updated in March 2024.</em>
            <br />
            <br />
            <Strong>Newly Funded AI Companies</Strong>:&nbsp;Total number of newly funded AI companies in the given
            country.&nbsp;<em>Source:&nbsp;Quid, 2023. Data last updated in March 2024.</em>
            <br />
            <br />
            <Strong>Relative AI Skill Penetration</Strong>:&nbsp;This indicator measures the intensity of AI skills
            within a country. It involves computing skill frequencies from LinkedIn members (2015-2023), re-weighting
            them using a TF-IDF model to identify the top 50 representative skills (the "skill genome"), and calculating
            the proportion of AI skills among these top skills. This rate indicates the prevalence and intensity of AI
            skills used by LinkedIn members in their jobs. To allow for skills penetration comparisons across countries,
            skill genomes are calculated and compared to a benchmark (e.g., global average). A ratio is then constructed
            between a country’s AI skills penetration and the benchmark, controlling for occupations. A relative AI
            skills penetration of 1.5 indicates that AI skills are 1.5 times more frequent than in the benchmark for
            similar occupations.&nbsp;<em>Source:&nbsp;LinkedIn, 2023. Data last updated in March 2024.</em>
            <br />
            <br />
            <Strong>AI Hiring Rate YoY Ratio</Strong>:&nbsp;This indicator measures the year-over-year change in the AI
            Hiring Rate relative to the Overall Hiring Rate in the same country. Each month, the AI Hiring Rate is
            calculated and divided by the Overall Hiring Rate. The year-over-year change of this ratio is then
            determined, followed by calculating a 12-month moving average. Interpretation: In 2023, India saw a 16.8%
            year-over-year growth in the ratio of AI talent hiring relative to overall hiring.&nbsp;
            <em>Source:&nbsp;LinkedIn, 2023. Data last updated in March 2024.</em>
            <br />
            <br />
            <Strong>AI Talent Concentration</Strong>:&nbsp;This indicator identifies AI talent as LinkedIn members who
            have explicitly added AI skills to their profile or work in AI-related occupations. AI Talent Concentration
            is calculated using the counts of AI talent relative to the total number of LinkedIn members in a given
            country. It is important to note that these metrics may be influenced by the level of LinkedIn coverage in
            different countries and should be interpreted with caution.&nbsp;
            <em>Source:&nbsp;LinkedIn, 2023. Data last updated in March 2024.</em>
            <br />
            <br />
            <Strong>AI Job Postings (% of Total)</Strong>:&nbsp;The percentage of job postings that require AI skills in
            a given country. Lightcast collects postings from over 51,000 online job sites to develop a comprehensive,
            real-time portrait of labor market demand.&nbsp;
            <em>Source:&nbsp;Lightcast, 2023. Data last updated in March 2024.</em>
            <br />
            <br />
            <Strong>Net Migration Flow of AI Skills</Strong>:&nbsp;AI talent migration per 10,000 LinkedIn members in a
            given country. This metric tracks AI talent with AI skills or jobs. For a specific country (country A), net
            talent flows are calculated by dividing the net AI talent migration by the Member Count, normalized per
            10,000 members. This indicates the relative migration of AI talent to and from country A.&nbsp;
            <em>Source:&nbsp;LinkedIn, 2023. Data last updated in March 2024.</em>
          </Paragraph>
        }
      />

      <DropDown
        headerComponent={<H4 style={{ display: 'inline' }}>Education</H4>}
        bodyComponent={
          <Paragraph>
            <Strong>AI Study Programs in English</Strong>:&nbsp;The number of English-language AI-related study programs
            in a given country. A study program, or degree program, comprises a series of courses designed to enable
            students to earn a relevant qualification, such as a degree or diploma.&nbsp;
            <em>Source:&nbsp;Studyportals, 2023. Data last updated in March 2024.</em>
            <br />
            <br />
            <Strong>AI Study Programs in English Penetration</Strong>:&nbsp;The proportion of AI-related study programs
            offered in English in a given country. A study program, or degree program, comprises a series of courses
            designed to enable students to earn a relevant qualification, such as a degree or diploma.&nbsp;
            <em>Source:&nbsp;Studyportals, 2023. Data last updated in March 2024.</em>
          </Paragraph>
        }
      />

      <DropDown
        headerComponent={<H4 style={{ display: 'inline' }}>Diversity</H4>}
        bodyComponent={
          <Paragraph>
            <Strong>AI Talent Concentration Gender Equality Index</Strong>:&nbsp;This indicator measures the
            distribution of AI talent between females and males among LinkedIn members in a given country, indicating
            gender equality within the AI talent pool.&nbsp;
            <em>Source:&nbsp;LinkedIn, 2023; AI Index, 2024. Data last updated in March 2024.</em>
          </Paragraph>
        }
      />

      <DropDown
        headerComponent={<H4 style={{ display: 'inline' }}>Policy and Governance</H4>}
        bodyComponent={
          <Paragraph>
            <Strong>National AI Strategy Presence</Strong>:&nbsp;This is a binary indicator showing whether a country
            has an AI strategy. This is a policy plan created by governments to guide the development and deployment of
            AI within their country. The AI Index conducted a web search to identify national AI strategies.&nbsp;
            <em>Source:&nbsp;AI Index, 2024. Data last updated in March 2024.</em>
            <br />
            <br />
            <Strong>AI Legislation Passed</Strong>:&nbsp;The number of AI-related bills that have been enacted in a
            given country.&nbsp;
            <em>Source:&nbsp;AI Index, 2024. Data last updated in March 2024.</em>
            <br />
            <br />
            <Strong>AI Mentions in Legislative Proceedings</Strong>:&nbsp; The number of times AI is mentioned in
            governmental and parliamentary proceedings in a given country.&nbsp;
            <em>Source:&nbsp;AI Index, 2024. Data last updated in March 2024.</em>
          </Paragraph>
        }
      />

      <DropDown
        headerComponent={<H4 style={{ display: 'inline' }}>Public Opinion</H4>}
        bodyComponent={
          <Paragraph>
            <Strong>Social Media Share of Voice on AI</Strong>:&nbsp;The proportion of social media discussions that
            mention AI compared to other topics in a given country.&nbsp;
            <em>Source:&nbsp;Quid, 2023. Data last updated in March 2024.</em>
            <br />
            <br />
            <Strong>AI Social Media Posts</Strong>:&nbsp;The number of social media posts that mention or discuss AI in
            a given country.&nbsp;
            <em>Source:&nbsp;Quid, 2023. Data last updated in March 2024.</em>
            <br />
            <br />
            <Strong>AI-Related Social Media Conversations Net Sentiment</Strong>:&nbsp;The overall sentiment of social
            media conversations about AI in a given country, calculated as the difference between positive and negative
            mentions. A net sentiment score of +100 means that all conversations are positive; a score of -100 means
            that all conversations are negative.&nbsp;
            <em>Source:&nbsp;Quid, 2023. Data last updated in March 2024.</em>
          </Paragraph>
        }
      />

      <DropDown
        headerComponent={<H4 style={{ display: 'inline' }}>Infrastructure</H4>}
        bodyComponent={
          <Paragraph>
            <Strong>Parts Semiconductor Devices Exports</Strong>:&nbsp;Total amount of exports in USD of electrical
            apparatus parts, including diodes, transistors, similar semiconductor devices, and photosensitive
            semiconductor devices for a given country.&nbsp;
            <em>Source:&nbsp;CEPII, 2024. Data last updated in March 2024.</em>
            <br />
            <br />
            <Strong>Supercomputers</Strong>:&nbsp;The number of supercomputers in a given country.&nbsp;
            <em>Source:&nbsp;Top500, 2023. Data last updated in March 2024.</em>
            <br />
            <br />
            <Strong>Compute Capacity (Rmax)</Strong>:&nbsp;The compute capacity, measured as Rmax (GFlops), representing
            the performance of supercomputers in a given country.&nbsp;
            <em>Source:&nbsp;Top500, 2023. Data last updated in March 2024.</em>
            <br />
            <br />
            <Strong>Internet Speed</Strong>:&nbsp; This indicator represents the median internet download speed in Mbps
            in a given country.&nbsp;
            <em>Source:&nbsp;Ookla, 2023. Data last updated in March 2024.</em>
          </Paragraph>
        }
      />
      <Box>
        <H3>Coverages and Nuances</H3>
        <Paragraph>
          To ensure the quality and representativeness of the analysis, a data coverage threshold of 70%, averaged over
          the last three years, was applied. This threshold was chosen to balance the need for comprehensive data with
          the goal of including a diverse set of countries. However, Estonia, Malaysia, Mexico, Russia, Saudi Arabia,
          and Turkey did not meet this threshold but have been included due to their strategic importance such as
          geopolitical influence, economic impact, or contributions to global trends. Therefore, these countries are
          included, but users should interpret their results with caution. For more detailed insights, please refer to
          the tables below.
        </Paragraph>
        <Paragraph>
          The <Strong>Data Coverage Scorecard by Country</Strong> shows which countries have available data for various
          indicators. The <Strong>Data Coverage (%) Per Year by Country</Strong> table displays the percentage of data
          coverage for each country over time, illustrating how data availability has evolved annually. The{' '}
          <Strong>Data Coverage (%) Per Year by Indicator</Strong> table highlights the percentage of countries that
          have data available for each indicator over the years, showing trends in data availability across different
          indicators. Most of the indicators in the Global AI Vibrancy Tool are featured in the latest AI Index report.
          For more detailed methodological information on any specific indicator, please refer to the most recent AI
          Index report.
        </Paragraph>
        <Paragraph>
          Users should exercise caution when interpreting rankings and drawing conclusions, as data completeness may
          vary between countries, indicators, and years. For instance, some indicators have more comprehensive coverage
          for certain nations than others. Results should be viewed in the context of data availability and coverage. If
          you have any questions about the data included in this tool, please reach out to the AI Index team. We also
          encourage community contributions to enhance the quality and comprehensiveness of the data used in this
          analysis. If you believe additional indicators should be included, or if you represent a country office aiming
          to improve data coverage for your country, please contact the AI Index team. We are always open to
          collaborations and hope the Global AI Vibrancy Tool can serve as a foundation for improving visibility into
          AI-related data worldwide.
        </Paragraph>
      </Box>
    </Box>
  )
}

export default InformationSection
